import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Stack,
  Button,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  Divider,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  GetApp as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { decryptResponse, downloadPDF, encryptResponse, getSessionKey } from '../../services';
import { themeBackgrounds } from '../../theme/theme';
import PageHeader from '../../components/PageHeader';
import { FREE_PLAN_ID } from '../../utils/common';
import { useNavigate } from 'react-router-dom';
import AnalyticsContent from '../../components/dashboard/AnalyticsContent';
import { userAnalyticsDataConfigs } from '../../configs/userAnalyticsDataConfig';
import { userAnalyticsStyleLayout } from '../../configs/userAnalyticsStyleConfig';
import { chatAnalyticsDataConfigs } from '../../configs/chatAnalyticsDataConfig';
import { chatAnalyticsStyleLayout } from '../../configs/chatAnalyticsStyleConfig';
import { callAnalyticsDataConfigs } from '../../configs/callAnalyticsDataConfig';
import { callAnalyticsStyleLayout } from '../../configs/callAnalyticsStyleConfig';
import { previewScheduleCallAnalyticsStyleLayout } from '../../configs/previewScheduleCallAnalyticsStyleConfig';
import previewUserAnalyticsData from '../../data/previewUserAnalyticsData.json';
import previewCallAnalyticsData from '../../data/previewCallAnalyticsData.json';
import previewScheduleCallAnalyticsData from '../../data/previewScheduleCallAnalyticsData.json';
import previewChatAnalyticsData from '../../data/previewChatAnalyticsData.json';
import { previewUserAnalyticsStyleLayout } from '../../configs/previewUserAnalyticsStyleConfig';
import { previewUserAnalyticsDataConfig } from '../../configs/previewUserAnalyticsDataConfig';
import { previewCallAnalyticsStyleLayout } from '../../configs/previewCallAnalyticsStyleConfig';
import { previewCallAnalyticsDataConfigs } from '../../configs/previewCallAnalyticsDataConfig';
import { previewScheduleCallAnalyticsDataConfigs } from '../../configs/previewScheduleCallAnalyticsDataConfig';
import CustomAgGridDailogBox from '../../components/CustomAgGridDailogBox';

const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('userAnalytics');
  const navigate = useNavigate();
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const applicationDetails = useSelector((state: any) => state.applicationDetails);
  const [userAnalyticsData, setUserAnalyticsData] = useState<any>(null);
  const [callsUsageData, setCallsUsageData] = useState<any>(null);
  const [scheduleCallsUsageData, setScheduleCallsUsageData] = useState<any>(null);
  const [chatUsageData, setChatUsageData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [freePlanDialogOpen, setFreePlanDialogOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [dateRange, setDateRange] = useState('last7days');
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [tempFromDate, setTempFromDate] = useState<Date | null>(null);
  const [tempToDate, setTempToDate] = useState<Date | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>('all_apps');
  const [applications, setApplications] = useState<any[]>([{ appId: 'all_apps', appName: 'All Applications' }]);
  const [gridDialogOpen, setGridDialogOpen] = useState(false);
  const [gridDialogTitle, setGridDialogTitle] = useState('');
  const [gridDialogData, setGridDialogData] = useState<any[]>([]);
  const [columnDefs, setColumnDefs] = useState<any[]>([
    { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
  ]);

  useEffect(() => {
    const range = getDateRange('last7days');
    setFromDate(range.from);
    setToDate(range.to);
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [userDetails]);

  useEffect(() => {
    if (userDetails?.plan_details.plan_id === FREE_PLAN_ID && !previewMode) {
      setPreviewMode(true);
      setFreePlanDialogOpen(true);
    }
  }, [userDetails, previewMode]);

  useEffect(() => {
    if (fromDate && toDate && userDetails?.plan_details.plan_id !== FREE_PLAN_ID) {
      fetchDataForActiveTab();
    }
  }, [activeTab, fromDate, toDate]);

  const fetchUserAnalyticsData = async () => {
    if (!userDetails?.organization_id || !fromDate || !toDate) {
      setError('Missing required user data or date range');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const payload = {
        request_id: "999999",
        timestamp: new Date().toISOString(),
        action: "get_chat_count_by_status",
        data: await encryptResponse({
          organization_id: userDetails.organization_id,
          user_id: userDetails.user_id,
          app_id: selectedApplicationId,
          from: fromDate.toISOString().split('T')[0],
          to: toDate.toISOString().split('T')[0]
        }, REQ_ENC_KEY),
      };

      type UserAnalyticsResponse = { status: number; data: any;[key: string]: any };
      const response = await httpService.post<UserAnalyticsResponse>(`${API_BASE_URL}/analytics/getUserAnalyticsData`, payload);

      if (response.data.status === 200 && response.data.data) {
        const data = response.data.data;
        const decrypted = await decryptResponse<{
          result: any;
        }>(data, getSessionKey() as string);
        const { result } = decrypted;
        setUserAnalyticsData(result);
      } else {
        setError('Failed to fetch user analytics data');
      }
    } catch (error) {
      setError('Network error while fetching data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCallsUsageData = async () => {
    console.log("Fetching calls usage data with dates:", fromDate, toDate, "userDetails:", userDetails, "applicationDetails:", applicationDetails);
    if (!userDetails?.organization_id || !fromDate || !toDate) {
      setError('Missing required user data or date range');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const payload = {
        request_id: "999999",
        timestamp: new Date().toISOString(),
        action: "get_chat_count_by_status",
        data: await encryptResponse({
          organization_id: userDetails.organization_id,
          user_id: userDetails.user_id,
          app_id: selectedApplicationId,
          from: fromDate.toISOString().split('T')[0],
          to: toDate.toISOString().split('T')[0]
        }, REQ_ENC_KEY),
      };

      type CallsUsageResponse = { status: number; data: any;[key: string]: any };
      const response = await httpService.post<CallsUsageResponse>(`${API_BASE_URL}/analytics/getUserCallAnalyticsData`, payload);

      if (response.data.status === 200 && response.data.data) {
        const data = response.data.data;
        const decrypted = await decryptResponse<{
          result: any;
        }>(data, getSessionKey() as string);
        const { result } = decrypted;
        setCallsUsageData(result);
      } else {
        setError('Failed to fetch calls usage data');
      }
    } catch (error) {
      setError('Network error while fetching data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchScheduleCallsUsageData = async () => {
    console.log("Fetching schedule calls usage data with dates:", fromDate, toDate, "userDetails:", userDetails, "applicationDetails:", applicationDetails);
    if (!userDetails?.organization_id || !fromDate || !toDate) {
      setError('Missing required user data or date range');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const payload = {
        request_id: "999999",
        data: await encryptResponse({
          organization_id: userDetails.organization_id,
          user_id: userDetails.user_id,
          app_id: selectedApplicationId,
          from: fromDate.toISOString().split('T')[0],
          to: toDate.toISOString().split('T')[0]
        }, REQ_ENC_KEY),
      };

      type ScheduleCallsUsageResponse = { status: number; data: any;[key: string]: any };
      const response = await httpService.post<ScheduleCallsUsageResponse>(`${API_BASE_URL}/analytics/getScheduleCallAnalyticsData`, payload);

      if (response.data.status === 200 && response.data.data) {
        const data = response.data.data;
        const decrypted = await decryptResponse<{
          result: any;
        }>(data, getSessionKey() as string);
        const { result } = decrypted;
        setScheduleCallsUsageData(result);
      } else {
        setError('Failed to fetch schedule calls usage data');
      }
    } catch (error) {
      setError('Network error while fetching data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchChatUsageData = async () => {
    if (!userDetails?.organization_id || !fromDate || !toDate) {
      setError('Missing required user data or date range');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const payload = {
        request_id: "999999",
        timestamp: new Date().toISOString(),
        action: "get_chat_count_by_status",
        data: await encryptResponse({
          organization_id: userDetails.organization_id,
          user_id: userDetails.user_id,
          app_id: selectedApplicationId,
          from: fromDate.toISOString().split('T')[0],
          to: toDate.toISOString().split('T')[0]
        }, REQ_ENC_KEY),
      };

      type ChatUsageResponse = { status: number; data: any;[key: string]: any };
      const response = await httpService.post<ChatUsageResponse>(`${API_BASE_URL}/analytics/getUserChatAnalyticsData`, payload);

      if (response.data.status === 200 && response.data.data) {
        const data = response.data.data;
        const decrypted = await decryptResponse<{
          result: any;
        }>(data, getSessionKey() as string);
        const { result } = decrypted;
        setChatUsageData(result);
      } else {
        setError('Failed to fetch chat usage data');
      }
    } catch (error) {
      setError('Network error while fetching data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllParticipants = async (params: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
    dateFilter?: boolean;
  } = {}) => {
    if (!userDetails?.organization_id || !fromDate || !toDate) {
      setError('Missing required user data or date range');
      return null;
    }

    // setIsLoading(true);
    setError(null);
    try {
      const payload = {
        request_id: "req_" + Date.now(),
        data: {
          organization_id: userDetails.organization_id,
          app_id: selectedApplicationId,
          from: fromDate.toISOString().split('T')[0],
          to: toDate.toISOString().split('T')[0],
          page: params.page || 1,
          limit: params.limit || 500,
          sort_by: params.sortBy || 'created_at',
          sort_order: params.sortOrder || 'desc',
          search: params.search || '',
          date_filter: params.dateFilter
        }
      };

      type ParticipantsResponse = {
        status: number;
        message: string;
        data: {
          data: any[];
          pagination: {
            total: number;
            page: number;
            limit: number;
            total_pages: number;
          }
        };
        error: any;
      };
      const response = await httpService.post<ParticipantsResponse>(
        `${API_BASE_URL}/analytics/grid/getAllParticipants`,
        payload
      );

      if (response.data.status === 200) {
        return response.data.data;
      } else {
        setError('Failed to fetch participants data');
        return null;
      }
    } catch (error) {
      setError('Network error while fetching participants data');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllGroups = async (params: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
    dateFilter?: boolean;
  } = {}) => {
    if (!userDetails?.organization_id || !fromDate || !toDate) {
      setError('Missing required user data or date range');
      return null;
    }

    setError(null);
    try {
      const payload = {
        request_id: "req_" + Date.now(),
        data: {
          organization_id: userDetails.organization_id,
          app_id: selectedApplicationId,
          from: fromDate.toISOString().split('T')[0],
          to: toDate.toISOString().split('T')[0],
          page: params.page || 1,
          limit: params.limit || 500,
          sort_by: params.sortBy || 'created_at',
          sort_order: params.sortOrder || 'desc',
          search: params.search || '',
          date_filter: params.dateFilter ?? false
        }
      };

      type GroupsResponse = {
        status: number;
        message: string;
        data: {
          data: any[];
          pagination: {
            total: number;
            page: number;
            limit: number;
            total_pages: number;
          }
        };
        error: any;
      };
      const response = await httpService.post<GroupsResponse>(
        `${API_BASE_URL}/analytics/grid/getAllGroups`,
        payload
      );

      if (response.data.status === 200) {
        return response.data.data;
      } else {
        setError('Failed to fetch groups data');
        return null;
      }
    } catch (error) {
      setError('Network error while fetching groups data');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTopChatUsers = async (userId: string, params: {
    page?: number;
    limit?: number;
  } = {}) => {
    if (!userDetails?.organization_id || !fromDate || !toDate || !userId) {
      setError('Missing required user data or date range');
      return null;
    }

    setError(null);
    try {
      const payload = {
        request_id: "req_" + Date.now(),
        data: {
          organization_id: userDetails.organization_id,
          app_id: selectedApplicationId,
          from: fromDate.toISOString().split('T')[0],
          to: toDate.toISOString().split('T')[0],
          user_id: userId,
          page: params.page || 1,
          limit: params.limit || 500
        }
      };

      type TopChatUsersResponse = {
        status: number;
        message: string;
        data: {
          data: any[];
          pagination: {
            total: number;
            page: number;
            limit: number;
            total_pages: number;
          }
        };
        error: any;
      };
      const response = await httpService.post<TopChatUsersResponse>(
        `${API_BASE_URL}/analytics/grid/getTopChatUsers`,
        payload
      );

      if (response.data.status === 200) {
        return response.data.data;
      } else {
        setError('Failed to fetch top chat users data');
        return null;
      }
    } catch (error) {
      setError('Network error while fetching top chat users data');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTopGroups = async (groupId: string, params: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
    dateFilter?: boolean;
  } = {}) => {
    if (!userDetails?.organization_id || !fromDate || !toDate || !groupId) {
      setError('Missing required user data or date range');
      return null;
    }

    setError(null);
    try {
      const payload = {
        request_id: "req_" + Date.now(),
        data: {
          organization_id: userDetails.organization_id,
          app_id: selectedApplicationId,
          group_id: groupId,
          from: fromDate.toISOString().split('T')[0],
          to: toDate.toISOString().split('T')[0],
          page: params.page || 1,
          limit: params.limit || 500,
          sort_by: params.sortBy || 'created_at',
          sort_order: params.sortOrder || 'desc',
          search: params.search || '',
          date_filter: params.dateFilter ?? false
        }
      };

      type TopGroupsResponse = {
        status: number;
        message: string;
        data: {
          data: any[];
          pagination: {
            total: number;
            page: number;
            limit: number;
            total_pages: number;
          }
        };
        error: any;
      };
      const response = await httpService.post<TopGroupsResponse>(
        `${API_BASE_URL}/analytics/grid/getAllGroups`,
        payload
      );

      if (response.data.status === 200) {
        return response.data.data;
      } else {
        setError('Failed to fetch top groups data');
        return null;
      }
    } catch (error) {
      setError('Network error while fetching top groups data');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNewUsersByDate = async (selectedDate: string, params: {
    page?: number;
    limit?: number;
    search?: string;
  } = {}) => {
    if (!userDetails?.organization_id || !selectedDate) {
      setError('Missing required user data or selected date');
      return null;
    }

    setError(null);
    try {
      const payload = {
        request_id: "req_" + Date.now(),
        data: {
          organization_id: userDetails.organization_id,
          app_id: selectedApplicationId,
          selected_date: selectedDate,
          page: params.page || 1,
          limit: params.limit || 500,
          search: params.search || ''
        }
      };

      type NewUsersByDateResponse = {
        status: number;
        message: string;
        data: {
          data: any[];
          pagination: {
            total: number;
            page: number;
            limit: number;
            total_pages: number;
          };
          selected_date: string;
        };
        error: any;
      };
      const response = await httpService.post<NewUsersByDateResponse>(
        `${API_BASE_URL}/analytics/grid/getNewUsersByDate`,
        payload
      );

      if (response.data.status === 200) {
        return response.data.data;
      } else {
        setError('Failed to fetch new users by date data');
        return null;
      }
    } catch (error) {
      setError('Network error while fetching new users by date data');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllCalls = async (params: {
    page?: number;
    limit?: number;
    search?: string;
  } = {}) => {
    if (!userDetails?.organization_id || !fromDate || !toDate) {
      setError('Missing required user data or date range');
      return null;
    }

    setError(null);
    try {
      const payload = {
        data: {
          organization_id: userDetails.organization_id,
          app_id: selectedApplicationId,
          from: fromDate.toISOString().split('T')[0],
          to: toDate.toISOString().split('T')[0],
          page: params.page || 1,
          limit: params.limit || 500,
          search: params.search || ''
        }
      };

      type AllCallsResponse = {
        status: number;
        message: string;
        data: {
          data: any[];
          pagination: {
            total: number;
            page: number;
            limit: number;
            total_pages: number;
          }
        };
        error: any;
      };
      const response = await httpService.post<AllCallsResponse>(
        `${API_BASE_URL}/analytics/grid/getAllCalls`,
        payload
      );

      if (response.data.status === 200) {
        return response.data.data;
      } else {
        setError('Failed to fetch all calls data');
        return null;
      }
    } catch (error) {
      setError('Network error while fetching all calls data');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCallsByDuration = async (durationRange: string, params: {
    page?: number;
    limit?: number;
  } = {}) => {
    if (!userDetails?.organization_id || !fromDate || !toDate || !durationRange) {
      setError('Missing required user data, date range, or duration range');
      return null;
    }

    setError(null);
    try {
      const payload = {
        data: {
          organization_id: userDetails.organization_id,
          app_id: selectedApplicationId,
          from: fromDate.toISOString().split('T')[0],
          to: toDate.toISOString().split('T')[0],
          page: params.page || 1,
          limit: params.limit || 500,
          duration_range: durationRange
        }
      };

      type CallsByDurationResponse = {
        status: number;
        message: string;
        data: {
          data: any[];
          pagination: {
            total: number;
            page: number;
            limit: number;
            total_pages: number;
          };
          filter: {
            min_duration: number;
            max_duration: number;
            duration_range: string;
          }
        };
        error: any;
      };
      const response = await httpService.post<CallsByDurationResponse>(
        `${API_BASE_URL}/analytics/grid/getCallsByDuration`,
        payload
      );

      if (response.data.status === 200) {
        return response.data.data;
      } else {
        setError('Failed to fetch calls by duration data');
        return null;
      }
    } catch (error) {
      setError('Network error while fetching calls by duration data');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCallsByDateAndType = async (selectedDate: string, params: {
    page?: number;
    limit?: number;
  } = {}) => {
    if (!userDetails?.organization_id || !selectedDate) {
      setError('Missing required user data or selected date');
      return null;
    }

    setError(null);
    try {
      const payload = {
        data: {
          organization_id: userDetails.organization_id,
          app_id: selectedApplicationId,
          selected_date: selectedDate,
          page: params.page || 1,
          limit: params.limit || 500
        }
      };

      type CallsByDateAndTypeResponse = {
        status: number;
        message: string;
        data: {
          data: any[];
          pagination: {
            total: number;
            page: number;
            limit: number;
            total_pages: number;
          };
          filter: {
            selected_date: string;
          }
        };
        error: any;
      };
      const response = await httpService.post<CallsByDateAndTypeResponse>(
        `${API_BASE_URL}/analytics/grid/getCallsByDateAndType`,
        payload
      );

      if (response.data.status === 200) {
        return response.data.data;
      } else {
        setError('Failed to fetch calls by date and type data');
        return null;
      }
    } catch (error) {
      setError('Network error while fetching calls by date and type data');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCallsByHour = async (selectedDate: string, selectedHour: number, callType?: string, params: {
    page?: number;
    limit?: number;
  } = {}) => {
    if (!userDetails?.organization_id || !selectedDate) {
      setError('Missing required user data or selected date/hour');
      return null;
    }

    setError(null);
    try {
      const payload = {
        data: {
          organization_id: userDetails.organization_id,
          app_id: selectedApplicationId,
          selected_date: selectedDate,
          selected_hour: selectedHour,
          page: params.page || 1,
          limit: params.limit || 500,
          ...(callType && { call_mode: callType })
        }
      };

      type CallsByHourResponse = {
        status: number;
        message: string;
        data: {
          data: any[];
          pagination: {
            total: number;
            page: number;
            limit: number;
            total_pages: number;
          };
          filter: {
            selected_date: string;
            selected_hour: number;
            call_type?: string;
          }
        };
        error: any;
      };
      const response = await httpService.post<CallsByHourResponse>(
        `${API_BASE_URL}/analytics/grid/getCallsByHour`,
        payload
      );

      if (response.data.status === 200) {
        return response.data.data;
      } else {
        setError('Failed to fetch calls by hour data');
        return null;
      }
    } catch (error) {
      setError('Network error while fetching calls by hour data');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCallsByType = async (callType: string, params: {
    page?: number;
    limit?: number;
  } = {}) => {
    if (!userDetails?.organization_id || !fromDate || !toDate || !callType) {
      setError('Missing required user data, date range, or call type');
      return null;
    }

    setError(null);
    try {
      const payload = {
        data: {
          organization_id: userDetails.organization_id,
          app_id: selectedApplicationId,
          from: fromDate.toISOString().split('T')[0],
          to: toDate.toISOString().split('T')[0],
          call_type: callType,
          page: params.page || 1,
          limit: params.limit || 500
        }
      };

      type CallsByTypeResponse = {
        status: number;
        message: string;
        data: {
          data: any[];
          pagination: {
            total: number;
            page: number;
            limit: number;
            total_pages: number;
          };
          call_type: string;
        };
        error: any;
      };
      const response = await httpService.post<CallsByTypeResponse>(
        `${API_BASE_URL}/analytics/grid/getCallsByType`,
        payload
      );

      if (response.data.status === 200) {
        return response.data.data;
      } else {
        setError('Failed to fetch calls by type data');
        return null;
      }
    } catch (error) {
      setError('Network error while fetching calls by type data');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Schedule call Grid API Integration

  const fetchScheduleCallsByStatus = async (
    status: string,
    params: {
      page?: number;
      limit?: number;
      sort_by?: string;
      sort_order?: 'asc' | 'desc';
    } = {}
  ) => {
    if (!userDetails?.organization_id || !fromDate || !toDate) {
      setError('Missing required user data or date range');
      return null;
    }

    setError(null);

    try {
      const payload = {
          organization_id: userDetails.organization_id,
          app_id: selectedApplicationId || 'all_apps',
          from: fromDate.toISOString().split('T')[0],
          to: toDate.toISOString().split('T')[0],
          status: status, // expired | completed | scheduled etc.
          page: params.page || 1,
          limit: params.limit || 500,
          sort_by: params.sort_by || 'createdAt',
          sort_order: params.sort_order || 'desc'
      };


      const response = await httpService.post<any>(
        `${API_BASE_URL}/analytics/grid/getScheduleMeetingsByStatus`,
        payload
      );

      if (response.data.status === 200) {
        return response.data.data;
      } else {
        setError(response.data.message || 'Failed to fetch scheduled meetings');
        return null;
      }
    } catch (error) {
      console.error('fetchScheduleCallsByStatus Error:', error);
      setError('Network error while fetching scheduled meetings');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchScheduleCallsByCallType = async (
    callType: string,
    params: {
      page?: number;
      limit?: number;
      sort_by?: string;
      sort_order?: 'asc' | 'desc';
    } = {}
  ) => {
    if (!userDetails?.organization_id || !fromDate || !toDate) {
      setError('Missing required user data or date range');
      return null;
    }

    setError(null);

    try {
      const payload = {
        organization_id: userDetails.organization_id,
        app_id: selectedApplicationId || 'all_apps',
        from: fromDate.toISOString().split('T')[0],
        to: toDate.toISOString().split('T')[0],
        call_type: callType,
        page: params.page || 1,
        limit: params.limit || 500,
        sort_by: params.sort_by || 'createdAt',
        sort_order: params.sort_order || 'desc'
      };


      const response = await httpService.post<any>(
        `${API_BASE_URL}/analytics/grid/getScheduleMeetingsByCallType`,
        payload
      );

      if (response.data.status === 200) {
        return response.data.data;
      } else {
        setError(response.data.message || 'Failed to fetch scheduled meetings');
        return null;
      }
    } catch (error) {
      console.error('fetchScheduleCallsByCallType Error:', error);
      setError('Network error while fetching scheduled meetings');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchScheduleCallsByHour = async (selectedDate: string, selectedHour: number, params: {
    page?: number;
    limit?: number;
  } = {}) => {
    if (!userDetails?.organization_id || !selectedDate) {
      setError('Missing required user data or selected date/hour');
      return null;
    }

    setError(null);
    try {
      const payload = {
        data: {
          organization_id: userDetails.organization_id,
          app_id: selectedApplicationId,
          selected_date: selectedDate,
          selected_hour: selectedHour,
          page: params.page || 1,
          limit: params.limit || 500,
        }
      };

      type CallsByHourResponse = {
        status: number;
        message: string;
        data: {
          data: any[];
          pagination: {
            total: number;
            page: number;
            limit: number;
            total_pages: number;
          };
          filter: {
            selected_date: string;
            selected_hour: number;
          }
        };
        error: any;
      };
      const response = await httpService.post<CallsByHourResponse>(
        `${API_BASE_URL}/analytics/grid/getScheduleCallsByHourGrid`,
        payload
      );

      if (response.data.status === 200) {
        return response.data.data;
      } else {
        setError('Failed to fetch calls by hour data');
        return null;
      }
    } catch (error) {
      setError('Network error while fetching calls by hour data');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchScheduleCallsByCreator = async (
    userId: string,
    params: {
      page?: number;
      limit?: number;
      sort_by?: string;
      sort_order?: 'asc' | 'desc';
    } = {}
  ) => {
    if (!userDetails?.organization_id || !fromDate || !toDate) {
      setError('Missing required user data or date range');
      return null;
    }

    setError(null);

    try {
      const payload = {
        organization_id: userDetails.organization_id,
        app_id: selectedApplicationId || 'all_apps',
        from: fromDate.toISOString().split('T')[0],
        to: toDate.toISOString().split('T')[0],
        creator_user_id: userId,
        page: params.page || 1,
        limit: params.limit || 500,
        sort_by: params.sort_by || 'createdAt',
        sort_order: params.sort_order || 'desc'
      };


      const response = await httpService.post<any>(
        `${API_BASE_URL}/analytics/grid/getScheduleMeetingsByCreator`,
        payload
      );

      if (response.data.status === 200) {
        return response.data.data;
      } else {
        setError(response.data.message || 'Failed to fetch scheduled meetings');
        return null;
      }
    } catch (error) {
      console.error('fetchScheduleCallsByCallType Error:', error);
      setError('Network error while fetching scheduled meetings');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseGridDialog = () => {
    setGridDialogOpen(false);
  };

  const handleDetailsClick = async (data?: any) => {
    console.log('Details clicked:', data);
    if (previewMode) {
      if (data?.key === 'users_overview.total_users') {
        const dataConfig = previewUserAnalyticsDataConfig.find(config => config.key === 'users_overview.total_users');
        const gridDataPass = previewUserAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_users');
        setGridDialogTitle(gridDataPass?.[0]?.title || 'Detailed Data View');
        setGridDialogData(gridDataPass?.[0]?.rows || []);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === 'users_overview.new_users') {
        const dataConfig = previewUserAnalyticsDataConfig.find(config => config.key === 'users_overview.new_users');
        const gridDataPass = previewUserAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_new_users');
        setGridDialogTitle(gridDataPass?.[0]?.title || 'Detailed Data View');
        setGridDialogData(gridDataPass?.[0]?.rows || []);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === 'average_group_size.total_groups') {
        const dataConfig = previewUserAnalyticsDataConfig.find(config => config.key === 'average_group_size.total_groups');
        const gridDataPass = previewUserAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_groups');
        setGridDialogTitle(gridDataPass?.[0]?.title || 'Detailed Data View');
        setGridDialogData(gridDataPass?.[0]?.rows || []);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === 'top_chat_users') {
        const dataConfig = previewUserAnalyticsDataConfig.find(config => config.key === 'top_chat_users');
        const gridDataPass = previewUserAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_chat_users');
        const userDataId = data?.itemData?.payload?.user_id || '';
        const totalChatUsersData: any[] = gridDataPass?.[0]?.rows || [];
        const filteredData = totalChatUsersData.filter((item: any) => item.participant_id === userDataId);
        console.log("Filtering for user id:", userDataId, totalChatUsersData);
        setGridDialogTitle(gridDataPass?.[0]?.title || 'Detailed Data View');
        setGridDialogData(filteredData);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "new_users_by_date") {
        const dataConfig = previewUserAnalyticsDataConfig.find(config => config.key === 'new_users_by_date');
        const gridDataPass = previewUserAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_users');
        const userCount = data?.itemData?.payload?.count || 0;
        const selectedDate = data?.itemData?.payload?.date || '';
        const filteredData = gridDataPass?.[0]?.rows.slice(0, userCount) || [];
        setGridDialogTitle(`New Users Details ${selectedDate} (${userCount} total)`);
        setGridDialogData(filteredData);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "top_groups_by_participants") {
        const dataConfig = previewUserAnalyticsDataConfig.find(config => config.key === 'top_groups_by_participants');
        const gridDataPass = previewUserAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_users');
        const userCount = data?.itemData?.payload?.participants || 0;
        const selectedGroup = data?.itemData?.payload?.name || '';
        const filteredData = gridDataPass?.[0]?.rows.slice(0, userCount) || [];
        setGridDialogTitle(`Top Groups Details ${selectedGroup} (${userCount} total)`);
        setGridDialogData(filteredData);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "chat_statistics_overview.total_messages") {
        const dataConfig = chatAnalyticsDataConfigs.find(config => config.key === 'chat_statistics_overview.total_messages');
        const gridDataPass = previewChatAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_messages');
        setGridDialogTitle("All Messages Data View");
        setGridDialogData(gridDataPass?.[0]?.rows || []);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "chat_statistics_overview.total_group_messages") {
        const dataConfig = chatAnalyticsDataConfigs.find(config => config.key === 'chat_statistics_overview.total_group_messages');
        const gridDataPass = previewChatAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_messages');
        const filteredGroupData = gridDataPass?.[0]?.rows.filter((item: any) => item.event_type === 'group_chat') || [];
        setGridDialogTitle("All Group Messages Data View");
        setGridDialogData(filteredGroupData);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "chat_statistics_overview.total_peer_messages") {
        const dataConfig = chatAnalyticsDataConfigs.find(config => config.key === 'chat_statistics_overview.total_peer_messages');
        const gridDataPass = previewChatAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_messages');
        const filteredPeerData = gridDataPass?.[0]?.rows.filter((item: any) => item.event_type === 'peer_chat') || [];
        setGridDialogTitle("All Peer Messages Data View");
        setGridDialogData(filteredPeerData);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "concurrent_message_sent_heatmap") {
        const dataConfig = chatAnalyticsDataConfigs.find(config => config.key === 'concurrent_message_sent_heatmap');
        const gridDataPass = previewChatAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_messages');
        const heatMapCount = data?.itemData?.value || 0;
        const selectedDate = data?.itemData?.date || '';
        const selectedHour = data?.itemData?.hour || '';
        const filteredPeerData = gridDataPass?.[0]?.rows.slice(0, heatMapCount) || [];
        setGridDialogTitle(`Sent Messages Details for ${selectedDate} at Hour ${selectedHour} (${heatMapCount} total)`);
        setGridDialogData(filteredPeerData);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "concurrent_message_delivered_heatmap") {
        const dataConfig = chatAnalyticsDataConfigs.find(config => config.key === 'concurrent_message_delivered_heatmap');
        const gridDataPass = previewChatAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_messages');
        const heatMapCount = data?.itemData?.value || 0;
        const selectedDate = data?.itemData?.date || '';
        const selectedHour = data?.itemData?.hour || '';
        const filteredPeerData = gridDataPass?.[0]?.rows.slice(0, heatMapCount) || [];
        setGridDialogTitle(`Delivered Messages Details for ${selectedDate} at Hour ${selectedHour} (${heatMapCount} total)`);
        setGridDialogData(filteredPeerData);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "concurrent_message_read_heatmap") {
        const dataConfig = chatAnalyticsDataConfigs.find(config => config.key === 'concurrent_message_read_heatmap');
        const gridDataPass = previewChatAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_messages');
        const heatMapCount = data?.itemData?.value || 0;
        const selectedDate = data?.itemData?.date || '';
        const selectedHour = data?.itemData?.hour || '';
        const filteredPeerData = gridDataPass?.[0]?.rows.slice(0, heatMapCount) || [];
        setGridDialogTitle(`Read Messages Details for ${selectedDate} at Hour ${selectedHour} (${heatMapCount} total)`);
        setGridDialogData(filteredPeerData);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "message_type_distribution") {
        const dataConfig = chatAnalyticsDataConfigs.find(config => config.key === 'message_type_distribution');
        const gridDataPass = previewChatAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_messages');
        const messageType = data?.itemData?.payload?.name || '';
        const filteredPeerData = gridDataPass?.[0]?.rows.filter((item: any) => item.message_type === messageType) || [];
        setGridDialogTitle(`(${messageType}) Messages Data View`);
        setGridDialogData(filteredPeerData);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "chat_statistics_overview.total_sent") {
        const dataConfig = chatAnalyticsDataConfigs.find(config => config.key === 'chat_statistics_overview.total_sent');
        const gridDataPass = previewChatAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_messages');
        const filteredPeerData = gridDataPass?.[0]?.rows.filter((item: any) => item.is_sent === true) || [];
        setGridDialogTitle(`Sent Messages Data View`);
        setGridDialogData(filteredPeerData);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "chat_statistics_overview.total_delivered") {
        const dataConfig = chatAnalyticsDataConfigs.find(config => config.key === 'chat_statistics_overview.total_delivered');
        const gridDataPass = previewChatAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_messages');
        const filteredPeerData = gridDataPass?.[0]?.rows.filter((item: any) => item.is_delivered === true) || [];
        setGridDialogTitle(`Delivered Messages Data View`);
        setGridDialogData(filteredPeerData);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "chat_statistics_overview.total_read") {
        const dataConfig = chatAnalyticsDataConfigs.find(config => config.key === 'chat_statistics_overview.total_read');
        const gridDataPass = previewChatAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_messages');
        const filteredPeerData = gridDataPass?.[0]?.rows.filter((item: any) => item.is_read === true) || [];
        setGridDialogTitle(`Read Messages Data View`);
        setGridDialogData(filteredPeerData);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "call_statistics_overview.total_calls") {
        const dataConfig = previewCallAnalyticsDataConfigs.find(config => config.key === "call_statistics_overview.total_calls");
        const gridDataPass = previewCallAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_calls');
        setGridDialogTitle("All Calls Data View");
        setGridDialogData(gridDataPass?.[0]?.rows || []);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "group_calls_heatmap") {
        const dataConfig = previewCallAnalyticsDataConfigs.find(config => config.key === "group_calls_heatmap");
        const gridDataPass = previewCallAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_calls');
        const selectedDate = data?.itemData?.date || '';
        const selectedHour = data?.itemData?.hour || '';
        const callCount = data?.itemData?.value || 0;
        const filteredCallData = gridDataPass?.[0]?.rows.filter((item: any) => item.call_mode === 'Group').slice(0, callCount) || [];
        setGridDialogTitle(`Group Calls Details for ${selectedDate} at Hour ${selectedHour} (${callCount} total)`);
        setGridDialogData(filteredCallData);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "peer_calls_heatmap") {
        const dataConfig = previewCallAnalyticsDataConfigs.find(config => config.key === "peer_calls_heatmap");
        const gridDataPass = previewCallAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_calls');
        const selectedDate = data?.itemData?.date || '';
        const selectedHour = data?.itemData?.hour || '';
        const callCount = data?.itemData?.value || 0;
        const filteredCallData = gridDataPass?.[0]?.rows.filter((item: any) => item.call_mode === 'Peer').slice(0, callCount) || [];
        setGridDialogTitle(`Peer Calls Details for ${selectedDate} at Hour ${selectedHour} (${callCount} total)`);
        setGridDialogData(filteredCallData);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      } else if (data?.key === "call_quality_distribution") {
        const dataConfig = previewCallAnalyticsDataConfigs.find(config => config.key === "peer_calls_heatmap");
        const gridDataPass = previewCallAnalyticsData?.grid_data?.filter((item: any) => item.key === 'total_calls');
        const dataFilter = data?.itemData?.payload?.name || '';
        const filteredCallData = gridDataPass?.[0]?.rows.filter((item: any) => item.call_type === dataFilter.toLowerCase()) || [];
        setGridDialogTitle(`(${dataFilter}) Calls Data View`);
        setGridDialogData(filteredCallData);
        setGridDialogOpen(true);
        setColumnDefs(dataConfig?.gridColDef || []);
      }
    } else {
      if (activeTab === 'userAnalytics') {
        if (data?.key === 'users_overview.total_users') {
          const participantsData = await fetchAllParticipants({
            page: 1,
            limit: 500,
            sortBy: 'created_at',
            sortOrder: 'desc',
            search: '',
            dateFilter: false
          });

          if (participantsData) {
            const participantsRows = participantsData.data || [];
            const pagination = participantsData.pagination || {};

            setGridDialogTitle(`All Participants (${pagination.total || 0} total)`);
            console.log("Participants Rows:", participantsRows);
            setGridDialogData(participantsRows);
            setColumnDefs([
              { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
              { field: "participant_id", headerName: "Participant ID", sortable: true, filter: true },
              { field: "participant_name", headerName: "Name", sortable: true, filter: true },
              { field: "app_id", headerName: "Application ID", sortable: true, filter: true },
              { field: "app_name", headerName: "Application Name", sortable: true, filter: true },
              { field: "participant_status", headerName: "Participant Status", sortable: true, filter: true },
              { field: "created_at", headerName: "Created At", sortable: true, filter: true },
            ]);
            setGridDialogOpen(true);
          }
        }
        if (data?.key === 'users_overview.new_users') {
          const participantsData = await fetchAllParticipants({
            page: 1,
            limit: 500,
            sortBy: 'created_at',
            sortOrder: 'desc',
            search: '',
            dateFilter: true
          });

          if (participantsData) {
            const participantsRows = participantsData.data || [];
            const pagination = participantsData.pagination || {};

            setGridDialogTitle(`New Participants (${pagination.total || 0} total)`);
            console.log("Participants Rows:", participantsRows);
            setGridDialogData(participantsRows);
            setColumnDefs([
              { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
              { field: "participant_id", headerName: "Participant ID", sortable: true, filter: true },
              { field: "participant_name", headerName: "Name", sortable: true, filter: true },
              { field: "app_id", headerName: "Application ID", sortable: true, filter: true },
              { field: "app_name", headerName: "Application Name", sortable: true, filter: true },
              { field: "participant_status", headerName: "Participant Status", sortable: true, filter: true },
              { field: "created_at", headerName: "Created At", sortable: true, filter: true },
            ]);
            setGridDialogOpen(true);
          }
        }
        if (data?.key === 'average_group_size.total_groups') {
          const groupsData = await fetchAllGroups({
            page: 1,
            limit: 500,
            sortBy: 'created_at',
            sortOrder: 'desc',
            search: '',
            dateFilter: false
          });

          if (groupsData) {
            const groupsRows = groupsData.data || [];
            const pagination = groupsData.pagination || {};

            setGridDialogTitle(`All Groups (${pagination.total || 0} total)`);
            console.log("Groups Rows:", groupsRows);
            setGridDialogData(groupsRows);
            setColumnDefs([
              { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
              { field: "room_id", headerName: "Room ID", sortable: true, filter: true },
              { field: "group_name", headerName: "Group Name", sortable: true, filter: true },
              { field: "type", headerName: "Type", sortable: true, filter: true },
              { field: "creation_type", headerName: "Creation Type", sortable: true, filter: true },
              { field: "participant_count", headerName: "Participants", sortable: true, filter: true },
              { field: "status", headerName: "Status", sortable: true, filter: true },
              { field: "created_at", headerName: "Created At", sortable: true, filter: true },
            ]);
            setGridDialogOpen(true);
          }
        }
        if (data?.key === 'top_chat_users') {
          const userId = data?.itemData?.payload?.user_id || '';
          const topChatUsersData = await fetchTopChatUsers(userId, {
            page: 1,
            limit: 500
          });

          if (topChatUsersData) {
            const chatUsersRows = topChatUsersData.data || [];
            const pagination = topChatUsersData.pagination || {};
            const userName = data?.itemData?.payload?.name || userId;

            setGridDialogTitle(`Chat Details for ${userName} (${pagination.total || 0} records)`);
            console.log("Top Chat Users Rows:", chatUsersRows);
            setGridDialogData(chatUsersRows);
            setColumnDefs([
              { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
              { field: "user_id", headerName: "User ID", sortable: true, filter: true },
              { field: "user_name", headerName: "User Name", sortable: true, filter: true },
              { field: "total_messages", headerName: "Total Messages", sortable: true, filter: true },
              { field: "peer_chats", headerName: "Peer Chats", sortable: true, filter: true },
              { field: "group_chats", headerName: "Group Chats", sortable: true, filter: true },
              { field: "last_active", headerName: "Last Active", sortable: true, filter: true },
            ]);
            setGridDialogOpen(true);
          }
        }
        if (data?.key === "top_groups") {
          const groupId = data?.itemData?.payload?.group_id || '';
          const topGroupsData = await fetchTopGroups(groupId, {
            page: 1,
            limit: 500,
            sortBy: 'created_at',
            sortOrder: 'desc',
            search: '',
            dateFilter: false
          });

          if (topGroupsData) {
            const groupsRows = topGroupsData.data || [];
            const groupName = data?.itemData?.payload?.name || groupId;

            // Flatten the participants array to create rows with group details
            const flattenedData: any[] = [];
            groupsRows.forEach((group: any) => {
              const participants = group.participants || [];
              participants.forEach((participant: any) => {
                flattenedData.push({
                  group_id: group.room_id,
                  group_name: group.group_name,
                  type: group.type,
                  creation_type: group.creation_type,
                  user_id: participant.user_id,
                  user_name: participant.user_name,
                  user_role: participant.user_role,
                  status: participant.status,
                  updated_at: participant.updated_at
                });
              });
            });

            setGridDialogTitle(`Group Participants for ${groupName} (${flattenedData.length} members)`);
            console.log("Top Groups Rows:", flattenedData);
            setGridDialogData(flattenedData);
            setColumnDefs([
              { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
              { field: "group_id", headerName: "Group ID", sortable: true, filter: true },
              { field: "group_name", headerName: "Group Name", sortable: true, filter: true },
              { field: "type", headerName: "Type", sortable: true, filter: true },
              { field: "creation_type", headerName: "Creation Type", sortable: true, filter: true },
              { field: "user_id", headerName: "User ID", sortable: true, filter: true },
              { field: "user_name", headerName: "User Name", sortable: true, filter: true },
              { field: "user_role", headerName: "User Role", sortable: true, filter: true },
              { field: "status", headerName: "Status", sortable: true, filter: true },
              { field: "updated_at", headerName: "Updated At", sortable: true, filter: true },
            ]);
            setGridDialogOpen(true);
          }
        }
        if (data?.key === "new_users_by_date") {
          const selectedDate = data?.itemData?.payload?.date || '';
          const newUsersByDateData = await fetchNewUsersByDate(selectedDate, {
            page: 1,
            limit: 500,
            search: ''
          });

          if (newUsersByDateData) {
            const usersRows = newUsersByDateData.data || [];
            const pagination = newUsersByDateData.pagination || {};
            const dateLabel = newUsersByDateData.selected_date || selectedDate;

            setGridDialogTitle(`New Users on ${dateLabel} (${pagination.total || 0} users)`);
            console.log("New Users By Date Rows:", usersRows);
            setGridDialogData(usersRows);
            setColumnDefs([
              { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
              { field: "participant_id", headerName: "Participant ID", sortable: true, filter: true },
              { field: "participant_name", headerName: "Name", sortable: true, filter: true },
              { field: "app_name", headerName: "Application Name", sortable: true, filter: true },
              { field: "created_at", headerName: "Created At", sortable: true, filter: true },
            ]);
            setGridDialogOpen(true);
          }
        }
        if (data?.key === "top_groups_by_participants") {
          const groupId = data?.itemData?.payload?.group_id || '';
          const topGroupsData = await fetchTopGroups(groupId, {
            page: 1,
            limit: 500,
          });
          if (topGroupsData) {
            const groupsRows = topGroupsData.data || [];
            const groupName = data?.itemData?.payload?.name || groupId;

            // Flatten the participants array to create rows with group details
            const flattenedData: any[] = [];
            groupsRows.forEach((group: any) => {
              const participants = group.participants || [];
              participants.forEach((participant: any) => {
                flattenedData.push({
                  group_id: group.room_id,
                  group_name: group.group_name,
                  type: group.type,
                  creation_type: group.creation_type,
                  user_id: participant.user_id,
                  user_name: participant.user_name,
                  user_role: participant.user_role,
                  status: participant.status,
                  updated_at: participant.updated_at
                });
              });
            });

            setGridDialogTitle(`Group Participants for ${groupName} (${flattenedData.length} members)`);
            console.log("Top Groups Rows:", flattenedData);
            setGridDialogData(flattenedData);
            setColumnDefs([
              { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
              { field: "group_id", headerName: "Group ID", sortable: true, filter: true },
              { field: "group_name", headerName: "Group Name", sortable: true, filter: true },
              { field: "type", headerName: "Type", sortable: true, filter: true },
              { field: "creation_type", headerName: "Creation Type", sortable: true, filter: true },
              { field: "user_id", headerName: "User ID", sortable: true, filter: true },
              { field: "user_name", headerName: "User Name", sortable: true, filter: true },
              { field: "user_role", headerName: "User Role", sortable: true, filter: true },
              { field: "status", headerName: "Status", sortable: true, filter: true },
              { field: "updated_at", headerName: "Updated At", sortable: true, filter: true },
            ]);
            setGridDialogOpen(true);
          }
        }
      } else if (activeTab === 'chatUsage') {

      } else if (activeTab === 'callsUsage') {
        if (data?.key === "call_statistics_overview.total_calls") {
          const callsData = await fetchAllCalls({
            page: 1,
            limit: 500,
            search: ''
          });

          if (callsData) {
            const callsRows = callsData.data || [];
            const pagination = callsData.pagination || {};

            setGridDialogTitle(`All Calls (${pagination.total || 0} total)`);
            console.log("Calls Rows:", callsRows);
            setGridDialogData(callsRows);
            setColumnDefs([
              { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
              { field: "room_id", headerName: "Room ID", sortable: true, filter: true },
              { field: "call_mode", headerName: "Call Mode", sortable: true, filter: true },
              { field: "call_type", headerName: "Call Type", sortable: true, filter: true },
              { field: "call_status", headerName: "Status", sortable: true, filter: true },
              { field: "call_duration", headerName: "Duration", sortable: true, filter: true },
              { field: "call_duration_minutes", headerName: "Duration (mins)", sortable: true, filter: true },
              { field: "number_of_participants", headerName: "Participants", sortable: true, filter: true },
              { field: "created_at", headerName: "Created At", sortable: true, filter: true },
              { field: "call_start_time", headerName: "Start Time", sortable: true, filter: true },
              { field: "call_end_time", headerName: "End Time", sortable: true, filter: true },
            ]);
            setGridDialogOpen(true);
          }
        }
        if (data?.key === "call_duration_distribution") {
          const callDurationRange = data?.itemData?.payload?.duration || '';
          const callsByDurationData = await fetchCallsByDuration(callDurationRange, {
            page: 1,
            limit: 500
          });

          if (callsByDurationData) {
            const callsRows = callsByDurationData.data || [];
            const pagination = callsByDurationData.pagination || {};
            const filter = callsByDurationData.filter || {};

            setGridDialogTitle(`Calls with Duration ${filter.duration_range || callDurationRange} (${pagination.total || 0} total)`);
            console.log("Calls by Duration Rows:", callsRows);
            setGridDialogData(callsRows);
            setColumnDefs([
              { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
              { field: "room_id", headerName: "Room ID", sortable: true, filter: true },
              { field: "call_type", headerName: "Call Type", sortable: true, filter: true },
              { field: "call_status", headerName: "Status", sortable: true, filter: true },
              { field: "call_duration", headerName: "Duration", sortable: true, filter: true },
              { field: "call_duration_minutes", headerName: "Duration (mins)", sortable: true, filter: true },
              { field: "number_of_participants", headerName: "Participants", sortable: true, filter: true },
              { field: "call_start_time", headerName: "Start Time", sortable: true, filter: true },
            ]);
            setGridDialogOpen(true);
          }
        }
        if(data?.key === "call_volume_by_type") {
          const rawDate = data?.itemData?.payload?.date || '';
          // Convert date from "Feb 18, 2026" to "2026-02-18"
          const selectedDate = rawDate ? new Date(rawDate + ' 12:00:00').toISOString().split('T')[0] : '';
          const callsByDateAndTypeData = await fetchCallsByDateAndType(selectedDate, {
            page: 1,
            limit: 500
          });

          if (callsByDateAndTypeData) {
            const callsRows = callsByDateAndTypeData.data || [];
            const pagination = callsByDateAndTypeData.pagination || {};
            const filter = callsByDateAndTypeData.filter || {};

            setGridDialogTitle(`Calls on ${filter.selected_date || selectedDate} (${pagination.total || 0} total)`);
            console.log("Calls by Date and Type Rows:", callsRows);
            setGridDialogData(callsRows);
            setColumnDefs([
              { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
              { field: "room_id", headerName: "Room ID", sortable: true, filter: true },
              { field: "call_mode", headerName: "Call Mode", sortable: true, filter: true },
              { field: "call_type", headerName: "Call Type", sortable: true, filter: true },
              { field: "call_status", headerName: "Status", sortable: true, filter: true },
              { field: "call_duration", headerName: "Duration", sortable: true, filter: true },
              { field: "number_of_participants", headerName: "Participants", sortable: true, filter: true },
              { field: "call_start_time", headerName: "Start Time", sortable: true, filter: true },
            ]);
            setGridDialogOpen(true);
          }
        }
        if(data?.key === "calls_heatmap" || data?.key === "group_calls_heatmap" || data?.key === "peer_calls_heatmap") {
            const rawDate = data?.itemData?.date || '';
            const selectedDate = rawDate ? new Date(rawDate + ' 12:00:00').toISOString().split('T')[0] : '';
            const selectedHour = data?.itemData?.hour || 0;
            const callTypeFilter = data?.key === "group_calls_heatmap" ? 'Group' : data?.key === "peer_calls_heatmap" ? 'Peer' : '';
            const callsByHourData = await fetchCallsByHour(selectedDate, selectedHour, callTypeFilter,{
              page: 1,
              limit: 500
            });

            if (callsByHourData) {
              const callsRows = callsByHourData.data || [];
              const pagination = callsByHourData.pagination || {};
              const filter = callsByHourData.filter || {};

              setGridDialogTitle(`Calls on ${filter.selected_date || selectedDate} at Hour ${filter.selected_hour ?? selectedHour} (${pagination.total || 0} total)`);
              setGridDialogData(callsRows);
              setColumnDefs([
                { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
                { field: "room_id", headerName: "Room ID", sortable: true, filter: true },
                { field: "call_mode", headerName: "Call Mode", sortable: true, filter: true },
                { field: "call_type", headerName: "Call Type", sortable: true, filter: true },
                { field: "call_status", headerName: "Status", sortable: true, filter: true },
                { field: "call_duration", headerName: "Duration", sortable: true, filter: true },
                { field: "call_start_time", headerName: "Start Time", sortable: true, filter: true },
              ]);
              setGridDialogOpen(true);
            }
        }
        if(data?.key === "call_quality_distribution") {
          const qualityFilter = data?.itemData?.payload?.name.toLowerCase() || '';
          const callsByTypeData = await fetchCallsByType(qualityFilter, {
            page: 1,
            limit: 500
          });

          if (callsByTypeData) {
            const callsRows = callsByTypeData.data || [];
            const pagination = callsByTypeData.pagination || {};
            const callTypeName = data?.itemData?.payload?.name || qualityFilter;

            setGridDialogTitle(`${callTypeName} Calls (${pagination.total || 0} total)`);
            console.log("Calls by Type Rows:", callsRows);
            setGridDialogData(callsRows);
            setColumnDefs([
              { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
              { field: "room_id", headerName: "Room ID", sortable: true, filter: true },
              { field: "call_type", headerName: "Call Type", sortable: true, filter: true },
              { field: "call_status", headerName: "Status", sortable: true, filter: true },
              { field: "call_duration", headerName: "Duration", sortable: true, filter: true },
              { field: "number_of_participants", headerName: "Participants", sortable: true, filter: true },
              { field: "call_start_time", headerName: "Start Time", sortable: true, filter: true },
            ]);
            setGridDialogOpen(true);
          }
        }
      } else if (activeTab === 'scheduleCallUsage') {
        if (data?.key === "schedule_statistics_overview.total_scheduled_meetings" || data?.key === "schedule_statistics_overview.scheduled" || data?.key === "schedule_statistics_overview.ongoing" || data?.key === "schedule_statistics_overview.completed" || data?.key === "schedule_statistics_overview.cancelled" || data?.key === "schedule_statistics_overview.expired") {
          let status = 'all';
          let titlepart = 'All Calls';
          const datakey = data?.key;
          if (datakey === 'schedule_statistics_overview.total_scheduled_meetings') {
            status = 'all';
            titlepart = 'All Calls'
          } else if (datakey === 'schedule_statistics_overview.scheduled') { 
            status = 'scheduled';
            titlepart = 'Upcoming Calls'
          } else if (datakey === 'schedule_statistics_overview.ongoing') {
            status = 'ongoing';
            titlepart = 'Ongoing Calls'
          } else if (datakey === 'schedule_statistics_overview.completed') {
            status = 'completed';
            titlepart = 'Completed Calls'
          } else if (datakey === 'schedule_statistics_overview.cancelled') {
            status = 'cancelled';
            titlepart = 'Cancelled Calls'
          } else if (datakey === 'schedule_statistics_overview.expired') {
            status = 'expired';
            titlepart = 'Expired Calls'
          }
          const scheduledCallsDetails = await fetchScheduleCallsByStatus(status, {
            page: 1,
            limit: 500,
          });
          if (scheduledCallsDetails) {
            console.log("Data printed ::",scheduledCallsDetails);
            const callsRows = scheduledCallsDetails.rows || [];
            const pagination = scheduledCallsDetails.pagination || {};

            setGridDialogTitle(`${titlepart} (${pagination.total || 0} total)`);
            console.log("Calls Rows:", callsRows);
            setGridDialogData(callsRows);
            setColumnDefs([
              { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
              { field: "room_id", headerName: "Room ID", sortable: true, filter: true },
              { field: "title", headerName: "Title", sortable: true, filter: true },
              { field: "description", headerName: "Description", sortable: true, filter: true },
              { field: "call_type", headerName: "Call Type", sortable: true, filter: true },
              { field: "creator_user_name", headerName: "Created By", sortable: true, filter: true },
              { field: "meeting_status", headerName: "Meeting Status", sortable: true, filter: true },
              { field: "timezone", headerName: "Time Zone", sortable: true, filter: true },
              { field: "meeting_start_time", headerName: "Start Time", sortable: true, filter: true },
            ]);
            setGridDialogOpen(true);
          }
          // console.log('total calls printed ::',totalCalls);
        }
        if(data?.key === 'schedule_call_type_distribution'){
          const payloadData = data?.itemData?.payload;
          const typeName = payloadData.name.toLowerCase().trim();
          if(payloadData && (typeName === 'video' || typeName === 'audio')){
            const scheduledCallsDetails = await fetchScheduleCallsByCallType(typeName, {
            page: 1,
            limit: 500,
          });
          if (scheduledCallsDetails) {
            console.log("Data printed ::",scheduledCallsDetails);
            const callsRows = scheduledCallsDetails.rows || [];
            const pagination = scheduledCallsDetails.pagination || {};

            setGridDialogTitle(`${typeName} (${pagination.total || 0} total)`);
            console.log("Calls Rows:", callsRows);
            setGridDialogData(callsRows);
            setColumnDefs([
              { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
              { field: "room_id", headerName: "Room ID", sortable: true, filter: true },
              { field: "title", headerName: "Title", sortable: true, filter: true },
              { field: "description", headerName: "Description", sortable: true, filter: true },
              { field: "call_type", headerName: "Call Type", sortable: true, filter: true },
              { field: "creator_user_name", headerName: "Created By", sortable: true, filter: true },
              { field: "meeting_status", headerName: "Meeting Status", sortable: true, filter: true },
              { field: "timezone", headerName: "Time Zone", sortable: true, filter: true },
              { field: "meeting_start_time", headerName: "Start Time", sortable: true, filter: true },
            ]);
            setGridDialogOpen(true);
          }
          }
          // fetchScheduleCallsByCallType
        }
        if (data?.key === 'schedule_calls_heatmap') {
          const rawDate = data?.itemData?.date || '';
          const selectedDate = rawDate ? new Date(rawDate + ' 12:00:00').toISOString().split('T')[0] : '';
          const selectedHour = data?.itemData?.hour || 0;
          const callsByHourData = await fetchScheduleCallsByHour(selectedDate, selectedHour, {
            page: 1,
            limit: 500
          });

          if (callsByHourData) {
            const callsRows = callsByHourData.data || [];
            const pagination = callsByHourData.pagination || {};
            const filter = callsByHourData.filter || {};

            setGridDialogTitle(`Calls on ${filter.selected_date || selectedDate} at Hour ${filter.selected_hour ?? selectedHour} (${pagination.total || 0} total)`);
            setGridDialogData(callsRows);
            setColumnDefs([
              { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
              { field: "room_id", headerName: "Room ID", sortable: true, filter: true },
              { field: "application_id", headerName: "Application ID", sortable: true, filter: true },
              { field: "creator_user_name", headerName: "Created By", sortable: true, filter: true },
              { field: "title", headerName: "Title", sortable: true, filter: true },
              { field: "description", headerName: "Description", sortable: true, filter: true },
              { field: "call_type", headerName: "Call Type", sortable: true, filter: true },
              { field: "meeting_start_time", headerName: "Start Time", sortable: true, filter: true },
              { field: "createdAt", headerName: "Created AT", sortable: true, filter: true },
            ]);
            setGridDialogOpen(true);
          }
        }
        if (data?.key === 'top_meeting_creators') {
          const payloadData = data?.itemData?.payload;
          const userId = payloadData.user_id;
          const userName = payloadData.name;
          const scheduledCallsDetails = await fetchScheduleCallsByCreator(userId, {
            page: 1,
            limit: 500,
          });
          if (scheduledCallsDetails) {
            console.log("Data printed ::", scheduledCallsDetails);
            const callsRows = scheduledCallsDetails.rows || [];
            const pagination = scheduledCallsDetails.pagination || {};

            setGridDialogTitle(`Scheduled By ${userName} (${pagination.total || 0} total)`);
            console.log("Calls Rows:", callsRows);
            setGridDialogData(callsRows);
            setColumnDefs([
              { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
              { field: "room_id", headerName: "Room ID", sortable: true, filter: true },
              { field: "title", headerName: "Title", sortable: true, filter: true },
              { field: "description", headerName: "Description", sortable: true, filter: true },
              { field: "call_type", headerName: "Call Type", sortable: true, filter: true },
              { field: "creator_user_name", headerName: "Created By", sortable: true, filter: true },
              { field: "meeting_status", headerName: "Meeting Status", sortable: true, filter: true },
              { field: "timezone", headerName: "Time Zone", sortable: true, filter: true },
              { field: "meeting_start_time", headerName: "Start Time", sortable: true, filter: true },
            ]);
            setGridDialogOpen(true);
          }
        }
      }
    }
  };

  const fetchDataForActiveTab = () => {
    if (previewMode) {
      return;
    }
    switch (activeTab) {
      case 'userAnalytics':
        fetchUserAnalyticsData();
        break;
      case 'callsUsage':
        fetchCallsUsageData();
        break;
      case 'chatUsage':
        fetchChatUsageData();
        break;
      case 'scheduleCallUsage':
        fetchScheduleCallsUsageData();
        break;
      default:
        break;
    }
  };

  const fetchApplications = async () => {
    const payload = {
      request_id: "notegtest1",
      timestamp: new Date().toISOString(),
      action: "get_all_applications",
      data: await encryptResponse({
        organization_id: userDetails?.organization_id || '',
        user_id: userDetails?.user_id || '',
      }, REQ_ENC_KEY)
    };
    try {
      const response = await httpService.post<any>(`${API_BASE_URL}/user/getAllApplications`, payload);
      if (response.data.status === 200 && response.data.data) {
        const data = response.data.data;
        const decrypted = await decryptResponse<{
          applications: any[];
        }>(data, getSessionKey() as string);
        const { applications } = decrypted;
        const fetchedApplications = applications || [];
        const appData = fetchedApplications.map((app: any) => ({ appId: app.app_id, appName: app.app_name }));
        const updatedApplications = [{ appId: 'all_apps', appName: 'All Applications' }, ...appData];
        setApplications(updatedApplications);
      } else {
        setApplications([]);
      }
    } catch (error) {
      setApplications([]);
    }
  };

  useEffect(() => {
    if (userDetails?.plan_details.plan_id !== FREE_PLAN_ID) {
      fetchDataForActiveTab();
    }
  }, [userDetails, selectedApplicationId]);

  const getAvailableReports = () => {
    switch (activeTab) {
      case 'userAnalytics':
        return [
          { id: 'counts-analytics', name: 'User Growth Over Time', description: 'Monthly user acquisition and retention data' },
          { id: 'top-ten-chat-users-graph', name: 'Top ten Chat Users', description: 'Most active users in chat' },
          { id: 'top-ten-chat-groups-graph', name: 'Top ten Chat Groups', description: 'Most active chat groups' },
          { id: 'new-user-graph-by-days', name: 'New Users by Day', description: 'User sign-ups by day' },
          { id: 'online-frequency-distribution-graph', name: 'User Online Frequency', description: 'User online frequency analysis' },
          { id: 'online-users-per-day-graph', name: 'Activity by Hour', description: 'Peak usage hours throughout the day' },
          { id: 'chat-status-distribution-graph', name: 'Chat Status Distribution', description: 'Distribution of chat statuses (e.g., sent, delivered, read)' },
          { id: 'group-wise-participants-counts-graph', name: 'Group-wise Participants', description: 'User participation in different groups' }
        ];
      case 'callsUsage':
        return [
          { id: 'call-volume', name: 'Daily Call Volume', description: 'Inbound and outbound call statistics' },
          { id: 'call-duration', name: 'Call Duration Distribution', description: 'Call length analysis and patterns' },
          { id: 'call-quality', name: 'Call Quality Distribution', description: 'Quality ratings and feedback' },
          { id: 'peak-hours', name: 'Peak Calling Hours', description: 'High-traffic time periods' },
          { id: 'call-errors', name: 'Call Errors and Issues', description: 'Technical problems and error analysis' },
          { id: 'regional-calls', name: 'Regional Call Distribution', description: 'Calls by geographic region' },
          { id: 'call-trends', name: 'Monthly Call Trends', description: 'Call volume and duration trends' },
          { id: 'success-rate', name: 'Call Success Rate', description: 'Successful vs failed call analysis' }
        ];
      case 'chatUsage':
        return [
          { id: 'message-volume', name: 'Daily Message Volume', description: 'Message count by day of week' },
          { id: 'active-chats', name: 'Active Chat Sessions', description: 'Real-time chat activity' },
          { id: 'response-time', name: 'Average Response Time', description: 'Chat response speed analysis' },
          { id: 'message-types', name: 'Message Types Distribution', description: 'Text, media, and file sharing stats' },
          { id: 'chat-duration', name: 'Chat Session Duration', description: 'Session length analysis' },
          { id: 'popular-hours', name: 'Most Popular Chat Hours', description: 'Peak messaging times' },
          { id: 'user-satisfaction', name: 'User Satisfaction Ratings', description: 'Chat experience ratings' },
          { id: 'chat-trends', name: 'Monthly Chat Trends', description: 'Message and session trends' }
        ];
      default:
        return [];
    }
  };

  // File format options
  const fileFormats = [
    { value: 'pdf', label: 'PDF Document', icon: <PdfIcon />, description: 'Formatted report with charts and graphics' },
    { value: 'excel', label: 'Excel Spreadsheet', icon: <ExcelIcon />, description: 'Raw data in spreadsheet format' },
    { value: 'csv', label: 'CSV File', icon: <CsvIcon />, description: 'Comma-separated values for data analysis' }
  ];

  // Date range options
  const dateRangeOptions = [
    { value: 'last7days', label: 'Last 7 days' },
    { value: 'last14days', label: 'Last 14 days' },
    { value: 'last4weeks', label: 'Last 4 weeks' },
    { value: 'custom', label: 'Custom range' },
  ];

  // Function to calculate date ranges
  const getDateRange = (option: string) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    switch (option) {
      case 'last7days':
        const last7days = new Date(today);
        last7days.setDate(last7days.getDate() - 7);
        return { from: last7days, to: today };
      case 'last14days':
        const last14days = new Date(today);
        last14days.setDate(last14days.getDate() - 14);
        return { from: last14days, to: today };
      case 'last4weeks':
        const last4weeks = new Date(today);
        last4weeks.setDate(last4weeks.getDate() - 28);
        return { from: last4weeks, to: today };
      default:
        return { from: fromDate, to: toDate };
    }
  };

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
    if (value === 'custom') {
      setCustomDateOpen(true);
      setTempFromDate(fromDate);
      setTempToDate(toDate);
    } else {
      const range = getDateRange(value);
      setFromDate(range.from);
      setToDate(range.to);
    }
  };

  const handleCustomDateApply = () => {
    setFromDate(tempFromDate);
    setToDate(tempToDate);
    setCustomDateOpen(false);
  };

  const handleCustomDateCancel = () => {
    setTempFromDate(fromDate);
    setTempToDate(toDate);
    setCustomDateOpen(false);
    setDateRange(fromDate && toDate ? 'custom' : 'last7days');
  };

  const getCurrentDateRange = () => {
    const range = getDateRange(dateRange);
    if (!range.from || !range.to) return '';

    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    };

    if (range.from.toDateString() === range.to.toDateString()) {
      return formatDate(range.from);
    }
    return `${formatDate(range.from)} - ${formatDate(range.to)}`;
  };

  const handleReportSelection = (reportId: string) => {
    setSelectedReports(prev => {
      if (prev.includes(reportId)) {
        return prev.filter(id => id !== reportId);
      } else {
        return [...prev, reportId];
      }
    });
  };

  const handleSelectAllReports = () => {
    const allReportIds = getAvailableReports().map(report => report.id);
    if (selectedReports.length === allReportIds.length) {
      setSelectedReports([]);
    } else {
      setSelectedReports(allReportIds);
    }
  };

  const getChartsForSelectedReports = () => {
    const charts: any[] = [];

    selectedReports.forEach(reportId => {
      switch (activeTab) {
        case 'userAnalytics':
          switch (reportId) {
            case 'counts-analytics':
              if (userAnalyticsData?.new_users_by_date?.chart_data) {
                charts.push({
                  type: 'bar',
                  title: 'New Users by Date',
                  description: 'This bar chart shows the number of new users who joined each day within the selected date range.',
                  data: userAnalyticsData.new_users_by_date.chart_data.map((item: any) => ({
                    name: item.date,
                    count: item.count
                  })),
                  dataKeys: ['count'],
                  xAxisKey: 'name',
                  colors: ['#F59E0B']
                });
              }
              break;
            case 'top-ten-chat-users-graph':
              break;
            case 'top-ten-chat-groups-graph':
              if (userAnalyticsData?.online_frequency_distribution?.chart_data) {
                charts.push({
                  type: 'bar',
                  title: 'Online Frequency Distribution',
                  description: 'This bar chart displays how often users are online during different time intervals.',
                  data: userAnalyticsData.online_frequency_distribution.chart_data.map((item: any) => ({
                    name: item.interval,
                    value: item.user_count
                  })),
                  dataKeys: ['value'],
                  xAxisKey: 'name',
                  colors: ['#8B5CF6']
                });
              }
              break;
            case 'new-user-graph-by-days':
              if (userAnalyticsData?.top_chat_users?.chart_data) {
                charts.push({
                  type: 'pie',
                  title: 'Top 10 Chat Users',
                  description: 'This pie chart displays the top 10 users who have sent the most messages during the selected time frame.',
                  data: userAnalyticsData.top_chat_users.chart_data.map((item: any) => ({
                    name: item.participant_name,
                    value: item.value,
                    color: item.color
                  }))
                });
              }
              break;
            case 'online-frequency-distribution-graph':
              if (userAnalyticsData?.top_groups?.chart_data) {
                charts.push({
                  type: 'pie',
                  title: 'Top 10 Chat Groups',
                  description: 'This pie chart illustrates the top 10 groups that have received the most messages during the selected time frame.',
                  data: userAnalyticsData.top_groups.chart_data.map((item: any) => ({
                    name: item.group_name,
                    value: item.value,
                    color: item.color
                  }))
                });
              }
              break;
            case 'online-users-per-day-graph':
              if (userAnalyticsData?.online_users_per_day?.chart_data) {
                charts.push({
                  type: 'line',
                  title: 'Online Users Per Day',
                  description: 'This line chart shows the number of unique online users and total online events per day.',
                  data: userAnalyticsData.online_users_per_day.chart_data.map((item: any) => ({
                    name: item.date,
                    users: item.unique_users_count,
                    events: item.total_online_events
                  })),
                  dataKeys: ['users', 'events'],
                  xAxisKey: 'name',
                  colors: ['#3B82F6', '#10B981']
                });
              }
              break;
            case 'chat-status-distribution-graph':
              if (userAnalyticsData?.chat_status_counts?.chat_counts) {
                charts.push({
                  type: 'pie',
                  title: 'Chat Status Distribution',
                  description: 'This pie chart shows the distribution of different chat statuses.',
                  data: userAnalyticsData.chat_status_counts.chat_counts.map((item: any) => ({
                    name: item.status,
                    value: item.count,
                    color: item.status === 'active' ? '#10B981' : item.status === 'inactive' ? '#EF4444' : '#F59E0B'
                  }))
                });
              }
              break;
            case 'group-wise-participants-counts-graph':
              if (userAnalyticsData?.chat_status_counts?.chat_counts) {
                charts.push({
                  type: 'pie',
                  title: 'Chat Status Distribution',
                  description: 'This pie chart shows the distribution of different chat statuses.',
                  data: userAnalyticsData.chat_status_counts.chat_counts.map((item: any) => ({
                    name: item.status,
                    value: item.count,
                    color: item.status === 'active' ? '#10B981' : item.status === 'inactive' ? '#EF4444' : '#F59E0B'
                  }))
                });
              }
              break;
          }
          break;

        case 'callsUsage':
          switch (reportId) {
            case 'call-volume':
              if (callsUsageData?.calls_per_day?.chart_data) {
                charts.push({
                  type: 'bar',
                  title: 'Daily Call Volume',
                  description: 'This bar chart shows the daily call volume including completed, failed, and abandoned calls.',
                  data: callsUsageData.calls_per_day.chart_data.map((item: any) => ({
                    name: item.date,
                    completed: item.completed_calls,
                    failed: item.failed_calls,
                    abandoned: item.abandoned_calls
                  })),
                  dataKeys: ['completed', 'failed', 'abandoned'],
                  xAxisKey: 'name',
                  colors: ['#10B981', '#EF4444', '#F59E0B']
                });
              }
              break;
            case 'call-duration':
              if (callsUsageData?.call_duration?.chart_data) {
                charts.push({
                  type: 'line',
                  title: 'Call Duration Trends',
                  description: 'This line chart displays call duration trends over time.',
                  data: callsUsageData.call_duration.chart_data.map((item: any) => ({
                    name: item.date,
                    duration: item.total_duration_minutes,
                    count: item.call_count
                  })),
                  dataKeys: ['duration', 'count'],
                  xAxisKey: 'name',
                  colors: ['#3B82F6', '#8B5CF6']
                });
              }
              break;
            case 'call-quality':
              // Add call quality chart based on available data
              break;
            case 'peak-hours':
              if (callsUsageData?.concurrent_calls_heatmap?.hourly_statistics) {
                charts.push({
                  type: 'bar',
                  title: 'Peak Calling Hours',
                  description: 'This bar chart shows average and maximum concurrent calls by hour.',
                  data: callsUsageData.concurrent_calls_heatmap.hourly_statistics.map((item: any) => ({
                    name: item.hour_label,
                    average: item.average_concurrent,
                    maximum: item.max_concurrent
                  })),
                  dataKeys: ['average', 'maximum'],
                  xAxisKey: 'name',
                  colors: ['#10B981', '#EF4444']
                });
              }
              break;
            case 'call-participation':
              if (callsUsageData?.call_participation?.chart_data) {
                charts.push({
                  type: 'line',
                  title: 'Call Participation Trends',
                  description: 'This line chart shows the total participants and average participants per call over time.',
                  data: callsUsageData.call_participation.chart_data.map((item: any) => ({
                    name: item.date,
                    total: item.total_participants,
                    average: item.average_participants_per_call
                  })),
                  dataKeys: ['total', 'average'],
                  xAxisKey: 'name',
                  colors: ['#F59E0B', '#8B5CF6']
                });
              }
              break;
          }
          break;

        case 'chatUsage':
          switch (reportId) {
            case 'message-volume':
              if (chatUsageData?.message_sent_daily?.chart_data) {
                charts.push({
                  type: 'bar',
                  title: 'Daily Message Volume',
                  description: 'This bar chart shows the number of messages sent each day.',
                  data: chatUsageData.message_sent_daily.chart_data.map((item: any) => ({
                    name: item.date,
                    count: item.count
                  })),
                  dataKeys: ['count'],
                  xAxisKey: 'name',
                  colors: ['#3B82F6']
                });
              }
              break;
            case 'message-types':
              if (chatUsageData?.message_types_sent?.chart_data) {
                charts.push({
                  type: 'bar',
                  title: 'Message Types Distribution',
                  description: 'This chart shows the distribution of different message types over time.',
                  data: chatUsageData.message_types_sent.chart_data.map((item: any) => ({
                    name: item.date,
                    text: item.text,
                    audio: item.audio
                  })),
                  dataKeys: ['text', 'audio'],
                  xAxisKey: 'name',
                  colors: ['#10B981', '#F59E0B']
                });
              }
              break;
            case 'response-time':
              if (chatUsageData?.message_status_per_day?.chart_data) {
                charts.push({
                  type: 'line',
                  title: 'Message Status Trends',
                  description: 'This line chart shows message delivery and read status trends over time.',
                  data: chatUsageData.message_status_per_day.chart_data.map((item: any) => ({
                    name: item.date,
                    sent: item.sent,
                    delivered: item.delivered,
                    read: item.read
                  })),
                  dataKeys: ['sent', 'delivered', 'read'],
                  xAxisKey: 'name',
                  colors: ['#3B82F6', '#10B981', '#F59E0B']
                });
              }
              break;
            case 'chat-duration':
              if (chatUsageData?.messages_by_conversation_type?.chart_data) {
                charts.push({
                  type: 'bar',
                  title: 'Messages by Conversation Type',
                  description: 'This chart shows peer vs group message distribution over time.',
                  data: chatUsageData.messages_by_conversation_type.chart_data.map((item: any) => ({
                    name: item.date,
                    peer: item.peer_messages,
                    group: item.group_messages
                  })),
                  dataKeys: ['peer', 'group'],
                  xAxisKey: 'name',
                  colors: ['#3B82F6', '#8B5CF6']
                });
              }
              break;
            case 'chat-trends':
              if (chatUsageData?.average_messages_across_conversations?.chart_data) {
                charts.push({
                  type: 'line',
                  title: 'Average Messages Per Conversation',
                  description: 'This line chart shows the average number of messages per conversation over time.',
                  data: chatUsageData.average_messages_across_conversations.chart_data.map((item: any) => ({
                    name: item.date,
                    average: item.average_messages_per_conversation
                  })),
                  dataKeys: ['average'],
                  xAxisKey: 'name',
                  colors: ['#10B981']
                });
              }
              break;
          }
          break;

        default:
          break;
      }
    });

    return charts;
  };

  const handleDownloadReport = () => {
    if (selectedReports.length === 0) {
      alert('Please select at least one report to download.');
      return;
    }

    const selectedReportNames = getAvailableReports()
      .filter(report => selectedReports.includes(report.id))
      .map(report => report.name);

    const charts = getChartsForSelectedReports();

    if (charts.length === 0) {
      alert('No data available for the selected reports. Please try again later.');
      return;
    }

    const dateRangeStr = getCurrentDateRange().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
    const filename = `${activeTab}_report_${dateRangeStr}.pdf`;

    downloadPDF({
      data: null,
      filename,
      title: `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Report`,
      format: selectedFormat as 'pdf',
      dateRange: getCurrentDateRange(),
      tab: activeTab,
      selectedReports: selectedReportNames,
      charts: charts
    });

    setReportDialogOpen(false);
    setSelectedReports([]);
  };

  const handleOpenReportDialog = () => {
    setReportDialogOpen(true);
    setSelectedReports([]);
  };

  const handlePreviewAnalytics = () => {
    setPreviewMode(true);
    setFreePlanDialogOpen(false);
  };

  const handleCancelFreePlan = () => {
    setFreePlanDialogOpen(false);
  };

  const renderUserAnalytics = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      );
    }

    if (!userAnalyticsData && !previewMode) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="text.secondary">
            No data available. Please check your date range and try again.
          </Typography>
        </Box>
      );
    }

    if (previewMode) {
      return (
        <>
          <AnalyticsContent dataConfigs={previewUserAnalyticsDataConfig} styleLayout={previewUserAnalyticsStyleLayout} analyticsData={previewUserAnalyticsData} onDetailsClick={handleDetailsClick} />
          <CustomAgGridDailogBox
            open={gridDialogOpen}
            onClose={handleCloseGridDialog}
            title={gridDialogTitle}
            rowData={gridDialogData}
            columnDefs={columnDefs}
            rowSelection='multiple'
            containerStyle={{
              height: '70vh'
            }}
            gridOptions={{
              suppressDragLeaveHidesColumns: true
            }}
            onRowSelected={(params) => {
              console.log('Selected Rows::', params.api.getSelectedRows());
            }}
            onCellClicked={(params) => {
              console.log('Cell clicked:', params);
            }}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
              flex: 1,
              cellStyle: { textAlign: 'left' }
            }}
          />
        </>
      );
    }

    const analyticsData = userAnalyticsData;
    return (
      <>
        <AnalyticsContent dataConfigs={userAnalyticsDataConfigs} styleLayout={userAnalyticsStyleLayout} analyticsData={analyticsData} onDetailsClick={handleDetailsClick} />
        <CustomAgGridDailogBox
          open={gridDialogOpen}
          onClose={handleCloseGridDialog}
          title={gridDialogTitle}
          rowData={gridDialogData}
          columnDefs={columnDefs}
          rowSelection='multiple'
          containerStyle={{
            height: '70vh'
          }}
          gridOptions={{
            suppressDragLeaveHidesColumns: true
          }}
          onRowSelected={(params) => {
            console.log('Selected Rows::', params.api.getSelectedRows());
          }}
          onCellClicked={(params) => {
            console.log('Cell clicked:', params);
          }}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            flex: 1,
            cellStyle: { textAlign: 'left' }
          }}
        />
      </>
    );
  };

  const renderCallsUsage = () => {
    if (isLoading && activeTab === 'callsUsage') {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      );
    }

    if (error && activeTab === 'callsUsage') {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      );
    }

    if (!callsUsageData && activeTab === 'callsUsage' && !previewMode) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="text.secondary">
            No data available. Please check your date range and try again.
          </Typography>
        </Box>
      );
    }

    if (previewMode && activeTab === 'callsUsage') {
      return (
        <>
          <AnalyticsContent dataConfigs={previewCallAnalyticsDataConfigs} styleLayout={previewCallAnalyticsStyleLayout} analyticsData={previewCallAnalyticsData} onDetailsClick={handleDetailsClick} />
          <CustomAgGridDailogBox
            open={gridDialogOpen}
            onClose={handleCloseGridDialog}
            title={gridDialogTitle}
            rowData={gridDialogData}
            columnDefs={columnDefs}
            rowSelection='multiple'
            containerStyle={{
              height: '70vh'
            }}
            gridOptions={{
              suppressDragLeaveHidesColumns: true
            }}
            onRowSelected={(params) => {
              console.log('Selected Rows::', params.api.getSelectedRows());
            }}
            onCellClicked={(params) => {
              console.log('Cell clicked:', params);
            }}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
              // flex: 1,
              cellStyle: { textAlign: 'left' }
            }}
          />
        </>
      );
    }
    return (
      <>
        <AnalyticsContent dataConfigs={callAnalyticsDataConfigs} styleLayout={callAnalyticsStyleLayout} analyticsData={callsUsageData} onDetailsClick={handleDetailsClick}/>
        <CustomAgGridDailogBox
          open={gridDialogOpen}
          onClose={handleCloseGridDialog}
          title={gridDialogTitle}
          rowData={gridDialogData}
          columnDefs={columnDefs}
          rowSelection='multiple'
          containerStyle={{
            height: '70vh'
          }}
          gridOptions={{
            suppressDragLeaveHidesColumns: true
          }}
          onRowSelected={(params) => {
            console.log('Selected Rows::', params.api.getSelectedRows());
          }}
          onCellClicked={(params) => {
            console.log('Cell clicked:', params);
          }}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            flex: 1,
            cellStyle: { textAlign: 'left' }
          }}
        />
      </>
    );
  };

  const renderScheduleCallsUsage = () => {
    if (isLoading && activeTab === 'scheduleCallUsage') {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      );
    }

    if (error && activeTab === 'scheduleCallUsage') {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      );
    }

    if (!scheduleCallsUsageData && activeTab === 'scheduleCallUsage' && !previewMode) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="text.secondary">
            No data available. Please check your date range and try again.
          </Typography>
        </Box>
      );
    }

    if (previewMode && activeTab === 'scheduleCallUsage') {
      return (
        <>
          <AnalyticsContent dataConfigs={previewScheduleCallAnalyticsDataConfigs} styleLayout={previewScheduleCallAnalyticsStyleLayout} analyticsData={previewScheduleCallAnalyticsData} />
          <CustomAgGridDailogBox
            open={gridDialogOpen}
            onClose={handleCloseGridDialog}
            title={gridDialogTitle}
            rowData={gridDialogData}
            columnDefs={columnDefs}
            rowSelection='multiple'
            containerStyle={{
              height: '70vh'
            }}
            gridOptions={{
              suppressDragLeaveHidesColumns: true
            }}
            onRowSelected={(params) => {
              console.log('Selected Rows::', params.api.getSelectedRows());
            }}
            onCellClicked={(params) => {
              console.log('Cell clicked:', params);
            }}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
              // flex: 1,
              cellStyle: { textAlign: 'left' }
            }}
          />
        </>
      );
    }
    return (
      <>
        <AnalyticsContent dataConfigs={previewScheduleCallAnalyticsDataConfigs} styleLayout={previewScheduleCallAnalyticsStyleLayout} analyticsData={scheduleCallsUsageData} onDetailsClick={handleDetailsClick}/>
        <CustomAgGridDailogBox
          open={gridDialogOpen}
          onClose={handleCloseGridDialog}
          title={gridDialogTitle}
          rowData={gridDialogData}
          columnDefs={columnDefs}
          rowSelection='multiple'
          containerStyle={{
            height: '70vh'
          }}
          gridOptions={{
            suppressDragLeaveHidesColumns: true
          }}
          onRowSelected={(params) => {
            console.log('Selected Rows::', params.api.getSelectedRows());
          }}
          onCellClicked={(params) => {
            console.log('Cell clicked:', params);
          }}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            flex: 1,
            cellStyle: { textAlign: 'left' }
          }}
        />
      </>
    );
  };

  const renderChatUsage = () => {
    if (isLoading && activeTab === 'chatUsage') {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      );
    }

    if (error && activeTab === 'chatUsage') {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      );
    }

    if (!chatUsageData && activeTab === 'chatUsage' && !previewMode) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="text.secondary">
            No data available. Please check your date range and try again.
          </Typography>
        </Box>
      );
    }

    // Show preview component in preview mode
    if (previewMode && activeTab === 'chatUsage') {
      return (
        <>
          <AnalyticsContent dataConfigs={chatAnalyticsDataConfigs} styleLayout={chatAnalyticsStyleLayout} analyticsData={previewChatAnalyticsData} onDetailsClick={handleDetailsClick} />
          <CustomAgGridDailogBox
            open={gridDialogOpen}
            onClose={handleCloseGridDialog}
            title={gridDialogTitle}
            rowData={gridDialogData}
            columnDefs={columnDefs}
            rowSelection='multiple'
            containerStyle={{
              height: '70vh'
            }}
            gridOptions={{
              suppressDragLeaveHidesColumns: true
            }}
            onRowSelected={(params) => {
              console.log('Selected Rows::', params.api.getSelectedRows());
            }}
            onCellClicked={(params) => {
              console.log('Cell clicked:', params);
            }}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
              cellStyle: { textAlign: 'left' }
            }}
          />
        </>
      );
    }
    return (
      <>
        <AnalyticsContent dataConfigs={chatAnalyticsDataConfigs} styleLayout={chatAnalyticsStyleLayout} analyticsData={chatUsageData} />
        <CustomAgGridDailogBox
          open={gridDialogOpen}
          onClose={handleCloseGridDialog}
          title={gridDialogTitle}
          rowData={gridDialogData}
          columnDefs={columnDefs}
          rowSelection='multiple'
          containerStyle={{
            height: '70vh'
          }}
          gridOptions={{
            suppressDragLeaveHidesColumns: true
          }}
          onRowSelected={(params) => {
            console.log('Selected Rows::', params.api.getSelectedRows());
          }}
          onCellClicked={(params) => {
            console.log('Cell clicked:', params);
          }}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            flex: 1,
            cellStyle: { textAlign: 'left' }
          }}
        />
      </>
    );
  };

  const tabItems = [
    { id: 'userAnalytics', label: 'User Analytics', icon: <AnalyticsIcon /> },
    { id: 'callsUsage', label: 'In App Calls Usage', icon: <PhoneIcon /> },
    { id: 'chatUsage', label: 'In App Chat Usage', icon: <ChatIcon /> },
    { id: 'scheduleCallUsage', label: 'Schedule Call Usage', icon: <CalendarIcon /> },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <PageHeader
          heading="Dashboard"
          subheading="Monitor your application performance, user engagement, and communication statistics"
          variant="gradient"
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
          <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {/* <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleOpenReportDialog}
                sx={{
                  background: themeBackgrounds.default,
                  color: '#ffffff',
                  '&:hover': {
                    background: themeBackgrounds.default,
                    opacity: 0.9,
                  },
                }}
              >
                Download Report
              </Button> */}
            </Stack>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <Box sx={{ minWidth: 200 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedApplicationId}
                  onChange={(e) => setSelectedApplicationId(e.target.value)}
                  displayEmpty
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  {applications.map((app) => (
                    <MenuItem key={app.appId} value={app.appId}>
                      {app.appName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {applications.find(app => app.appId === selectedApplicationId)?.appName || 'Select Application'}
              </Typography>
            </Box>

            <Box sx={{ minWidth: 200 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={dateRange}
                  onChange={(e) => handleDateRangeChange(e.target.value)}
                  startAdornment={<CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                  displayEmpty
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  {dateRangeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {getCurrentDateRange() && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {getCurrentDateRange()}
                </Typography>
              )}
            </Box>
          </Stack>
        </Stack>
      </Box>

      <Dialog
        open={freePlanDialogOpen}
        onClose={handleCancelFreePlan}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Stack direction="row" alignItems="flex-start" spacing={1.5}>
              <Box
                sx={{
                  backgroundColor: 'primary.main',
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <AnalyticsIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600" sx={{ lineHeight: 1.3 }}>
                  Analytics Not Available
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  on Free Plan
                </Typography>
              </Box>
            </Stack>
            <Button
              onClick={handleCancelFreePlan}
              sx={{
                minWidth: 'auto',
                p: 0.75,
                color: 'text.secondary',
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3 }}>
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              Analytics features are available only on paid plans. Upgrade your plan to unlock full access to real-time insights, usage reports, and detailed analytics for your application.
            </Typography>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100px',
                  height: '100px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '50%',
                  transform: 'translate(30%, -30%)'
                }
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Typography sx={{ fontSize: 24, flexShrink: 0 }}>💡</Typography>
                <Box>
                  <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5 }}>
                    Preview Available
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.95, fontSize: '0.875rem' }}>
                    You can still preview how analytics will look by viewing sample analytics data.
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1.5 }}>
          <Button
            onClick={handlePreviewAnalytics}
            variant="outlined"
            fullWidth
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'primary.lighter'
              }
            }}
          >
            <AnalyticsIcon sx={{ mr: 1, fontSize: 20 }} />
            Preview Analytics
          </Button>
          <Button
            onClick={() => navigate('/plans-and-billing')}
            variant="contained"
            fullWidth
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                background: 'linear-gradient(135deg, #5568d3 0%, #6b3f91 100%)'
              }
            }}
          >
            <TrendingUpIcon sx={{ mr: 1, fontSize: 20 }} />
            Upgrade Plan
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={customDateOpen}
        onClose={handleCustomDateCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Custom Date Range</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="From Date"
              type="date"
              value={tempFromDate ? tempFromDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setTempFromDate(e.target.value ? new Date(e.target.value) : null)}
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              label="To Date"
              type="date"
              value={tempToDate ? tempToDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setTempToDate(e.target.value ? new Date(e.target.value) : null)}
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: tempFromDate ? tempFromDate.toISOString().split('T')[0] : undefined,
                max: new Date().toISOString().split('T')[0]
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCustomDateCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleCustomDateApply}
            variant="contained"
            disabled={!tempFromDate || !tempToDate}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Download Reports</Typography>
            <Chip
              label={`${tabItems.find(tab => tab.id === activeTab)?.label || 'Reports'}`}
              size="small"
              color="primary"
            />
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Date Range: {getCurrentDateRange() || 'No date range selected'}
              </Typography>
            </Box>

            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Select Reports</Typography>
                <Button
                  size="small"
                  onClick={handleSelectAllReports}
                  sx={{ textTransform: 'none' }}
                >
                  {selectedReports.length === getAvailableReports().length ? 'Deselect All' : 'Select All'}
                </Button>
              </Stack>

              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                <List>
                  {getAvailableReports().map((report, index) => (
                    <React.Fragment key={report.id}>
                      <ListItem sx={{ py: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedReports.includes(report.id)}
                              onChange={() => handleReportSelection(report.id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1" fontWeight="medium">
                                {report.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {report.description}
                              </Typography>
                            </Box>
                          }
                          sx={{ width: '100%', margin: 0 }}
                        />
                      </ListItem>
                      {index < getAvailableReports().length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>

              {selectedReports.length > 0 && (
                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                  {selectedReports.length} report(s) selected
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>File Format</Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedFormat}
                  onChange={(e) => setSelectedFormat(e.target.value)}
                >
                  {fileFormats.map((format) => (
                    <MenuItem key={format.value} value={format.value}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {format.icon}
                        <Box>
                          <Typography variant="body1">{format.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDownloadReport}
            variant="contained"
            disabled={selectedReports.length === 0}
            startIcon={<DownloadIcon />}
          >
            Download {selectedReports.length > 0 ? `(${selectedReports.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper sx={{ mb: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={0}>
            {tabItems.map((tab) => (
              <Button
                key={tab.id}
                startIcon={tab.icon}
                onClick={() => setActiveTab(tab.id)}
                disabled={isLoading}
                sx={{
                  py: 2,
                  px: 3,
                  borderRadius: 0,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  color: activeTab === tab.id ? 'primary.main' : 'text.secondary',
                  borderBottom: activeTab === tab.id ? 2 : 0,
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {tab.label}
              </Button>
            ))}
          </Stack>
        </Box>
      </Paper>

      {previewMode && (
        <Paper
          sx={{
            mb: 2,
            p: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <AnalyticsIcon sx={{ fontSize: 28 }} />
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Preview Mode - Sample Data
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  You are viewing sample analytics data. Upgrade your plan to access real-time analytics.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/plans-and-billing')}
              sx={{
                color: 'white',
                borderColor: 'white',
                flexShrink: 0,
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Upgrade Plan
            </Button>
          </Stack>
        </Paper>
      )}

      <Box sx={{ width: '100%', background: themeBackgrounds.lightGrey }}>
        <Box sx={{ padding: 1 }}>
          {activeTab === 'userAnalytics' && renderUserAnalytics()}
          {activeTab === 'callsUsage' && renderCallsUsage()}
          {activeTab === 'chatUsage' && renderChatUsage()}
          {activeTab === 'scheduleCallUsage' && renderScheduleCallsUsage()}
        </Box>
      </Box>
    </Box>
  );
}

export default Dashboard;
